import type { DocumentChecklistItem } from '../mocks/criminalMasterData';
import type { CriminalDocumentRow, CriminalWizardDraft } from '../types/criminalCaseRequest';

/** Offence Affecting Human Body / Life — API id and mock id. */
export const SUB_CATEGORY_OHBL_ID = '7aa4434d-4d18-4144-afbe-43dfb8afeb90';
export const SUB_CATEGORY_OHBL_MOCK_ID = 'human-body';

const PMR_MANDATORY_AREA_IDS = new Set([
  '71557329-9326-480e-a125-1f68bfbae75c',
  '19f3d695-00e4-461b-a804-3f96440af90b',
]);

const PMR_MANDATORY_AREA_NAMES = new Set(['murder', 'causing death by negligence']);

type CategorizationSlice = Pick<
  CriminalWizardDraft,
  'subFileCategoryId' | 'subFileCategoryName' | 'applicableAreaIds' | 'applicableAreas'
>;

export function isOffenceAffectingHumanBodySubCategory(
  draft: Pick<CriminalWizardDraft, 'subFileCategoryId' | 'subFileCategoryName'>,
): boolean {
  const id = draft.subFileCategoryId;
  if (id === SUB_CATEGORY_OHBL_ID || id === SUB_CATEGORY_OHBL_MOCK_ID) return true;
  return draft.subFileCategoryName.trim().toLowerCase().includes('offence affecting human body');
}

export function hasPmrMandatoryApplicableArea(
  draft: Pick<CriminalWizardDraft, 'applicableAreaIds' | 'applicableAreas'>,
): boolean {
  if (draft.applicableAreaIds.some((areaId) => PMR_MANDATORY_AREA_IDS.has(areaId))) return true;
  return draft.applicableAreas.some((area) => PMR_MANDATORY_AREA_NAMES.has(area.trim().toLowerCase()));
}

/** Returns PMR mandatory flag for OHBL, or null when the rule does not apply. */
export function resolvePmrMandatory(draft: CategorizationSlice): boolean | null {
  if (!isOffenceAffectingHumanBodySubCategory(draft)) return null;
  return hasPmrMandatoryApplicableArea(draft);
}

export function isPmrDocumentName(name: string): boolean {
  const n = name.trim().toUpperCase();
  return n === 'PMR' || n.startsWith('PMR-') || n.startsWith('PMR ');
}

export function isPmrChecklistItem(item: {
  documentTypeCode?: string;
  name: string;
}): boolean {
  if (item.documentTypeCode?.trim().toUpperCase() === 'PMR') return true;
  return isPmrDocumentName(item.name);
}

export function applyPmrMandatoryToChecklist(
  checklist: DocumentChecklistItem[],
  draft: CategorizationSlice,
): DocumentChecklistItem[] {
  const pmrMandatory = resolvePmrMandatory(draft);
  if (pmrMandatory === null) return checklist;
  return checklist.map((item) =>
    isPmrChecklistItem(item) ? { ...item, mandatory: pmrMandatory } : item,
  );
}

export function applyPmrMandatoryToDocuments(
  documents: CriminalDocumentRow[],
  draft: CategorizationSlice,
): CriminalDocumentRow[] {
  const pmrMandatory = resolvePmrMandatory(draft);
  if (pmrMandatory === null) return documents;
  return documents.map((doc) =>
    isPmrDocumentName(doc.documentName) ? { ...doc, mandatory: pmrMandatory } : doc,
  );
}
